import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    colors,
    Divider,
    Grid,
    Table,
    TableBody,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import useRouter from 'use-react-router';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import TableRowTextField from '../../../components/TableRowTextField/TableRowTextField';
import TableRowToggle from '../../../components/TableRowToggle/TableRowToggle';
import {Permission, Role} from '../../../graphql/auth';
import getPermissions from '../../../graphql/auth/getPermissions.api';
import anyLoading from '../../../utils/anyLoading';
import createRole from './createRole.api';
import deleteRole from './deleteRole.api';
import updateRole from './updateRole.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    card: {
        padding: 0,
    },
    divider: {
        //backgroundColor: colors.grey[300]
    },
    titleCell: {
        width: '40%',
    },
    dataCell: {
        width: '60%',
    },
    actions: {
        justifyContent: 'flex-end'
    },
    saveButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        color: theme.palette.white,
        backgroundColor: theme.palette.error.main,
        '&:hover': {
            backgroundColor: theme.palette.error.dark
        }
    },
}));

const RoleEdit = (props: { role: Role, }) => {
    const {role} = props;
    const classes = useStyles();
    const {history} = useRouter();
    const [sendCreate, createData] = createRole();
    const [sendUpdate, updateData] = updateRole();
    const [sendDelete, deleteData] = deleteRole();
    const loadingData = anyLoading(createData, updateData, deleteData);
    const [formState, setFormState] = useState({...role});
    const [openDelete, setOpenDelete] = useState(false);
    const permissionsData = getPermissions();
    const permissions = permissionsData.loading ? [] : _.orderBy(permissionsData.data!.permissions, 'ident');
    const newRole = role.id === -1;
    const {enqueueSnackbar} = useSnackbar();

    const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        event.persist();
        setFormState((oldState: any) => ({
            ...oldState,
            [event.target.name]:
                event.target.type === 'checkbox'
                    ? (event.target as HTMLInputElement).checked
                    : event.target.value
        }));
    };

    const handlePermissionChange = (permission: Permission, active: boolean) => {
        const mutatePermissions: { connect?: number[], disconnect?: number[] } = {};
        mutatePermissions.connect = !active ? [permission.id] : [];
        mutatePermissions.disconnect = active ? [permission.id] : [];
        const sanitisedData = {
            id: role.id,
            permissions: mutatePermissions,
        };

        let optimisticPermissions = [...role.permissions!];

        if (active) {
            optimisticPermissions = optimisticPermissions.filter(item => item.id !== permission.id);
        } else {
            optimisticPermissions.push(permission);
        }

        sendUpdate({
            variables: {data: sanitisedData},
            optimisticResponse: {
                __typename: 'Mutation',
                updateRole: {
                    ...formState,
                    __typename: 'Role',
                    permissions: optimisticPermissions,
                },
            }
        });

        setFormState((oldState: any) => ({
            ...oldState,
            permissions: optimisticPermissions,
        }));
    };

    const onSaveClick = () => {

        if (newRole) {
            const sanitisedData = {
                level: 3,
                name: formState.name,
                ident: formState.ident,
                description: formState.description,
                active: formState.active,
            };

            sendCreate({
                variables: {data: sanitisedData},
            })
                .then((response) => {
                    if (response.data && response.data.createRole.id) {
                        const path = `/roles/${response.data.createRole.id}`;
                        enqueueSnackbar('Role created', {variant: 'success'});
                        history.push(path);
                    } else {
                        enqueueSnackbar('Role creation failed', {variant: 'error'});
                    }
                })
                .catch(() => {
                    enqueueSnackbar('Role creation failed', {variant: 'error'});
                });
        } else {
            const sanitisedData = {
                id: formState.id,
                name: formState.name,
                ident: formState.ident,
                description: formState.description,
                active: formState.active,
            };

            sendUpdate({
                variables: {data: sanitisedData},
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateRole: {...formState, __typename: 'Role',},
                }
            })
                .then(() => {
                    enqueueSnackbar('Role updated', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Update failed', {variant: 'error'});
                });
        }
    };

    const onDeleteClick = () => {
        sendDelete({variables: {id: role.id}})
            .then(() => {
                enqueueSnackbar('Role deleted', {variant: 'success'});
                history.push('/roles');
            })
            .catch(() => {
                enqueueSnackbar('Delete failed', {variant: 'error'});
            });
    };

    const handleDeleteOpen = () => {
        setOpenDelete(true);
    };

    const handleDeleteClose = () => {
        setOpenDelete(false);
    };

    return (
        <form>
            <Grid className={classes.root} container spacing={3}>

                <Grid item xs={12}>
                    <Typography variant="h2">Role: {role.name}</Typography>
                </Grid>
                <Grid item md={6} sm={12}>
                    <Card className={classes.card}>
                        <LoadingOverlay loading={loadingData.loading}>
                            <CardHeader title="Role Details"/>
                            <Divider/>
                            <CardContent className={classes.card}>
                                <Table className={classes.card}>
                                    <TableBody>
                                        <TableRowToggle name="active" title="active?" onChange={handleFieldChange}
                                                        value={formState.active}/>
                                        <TableRowTextField name="name" title="Name" onChange={handleFieldChange}
                                                           value={formState.name}/>
                                        <TableRowTextField name="ident" title="Ident" onChange={handleFieldChange}
                                                           value={formState.ident}/>
                                        <TableRowTextField name="description" title="Description"
                                                           onChange={handleFieldChange} value={formState.description}/>
                                    </TableBody>
                                </Table>
                            </CardContent>
                            <CardActions className={classes.actions}>
                                {!newRole && role.level! > 2 && (
                                    <Can permission="role.delete">
                                        <Button className={classes.deleteButton} variant="contained" size="small"
                                                onClick={handleDeleteOpen}>
                                            Delete
                                        </Button>
                                    </Can>
                                )}
                                <Button variant="contained" component={RouterLink} size="small" to={`/roles`}>
                                    Cancel
                                </Button>
                                <Button className={classes.saveButton} onClick={onSaveClick}
                                        variant="contained">Save</Button>
                            </CardActions>
                        </LoadingOverlay>
                    </Card>
                </Grid>

                {!newRole && (
                    <Grid item md={6} sm={12}>
                        <LoadingOverlay loading={permissionsData.loading}>
                            {!permissionsData.loading && (
                                <Card className={classes.card}>
                                    <CardHeader title="Permissions"/>
                                    <Divider/>
                                    <CardContent className={classes.card}>
                                        <Table className={classes.card}>
                                            <TableBody>
                                                {permissions.map((permission) => permission.ident !== '*' && (
                                                    <Can key={permission.ident} permission={permission.ident}>
                                                        <TableRowToggle name={permission.ident!}
                                                                        title={permission.ident!}
                                                                        onChange={(event) => handlePermissionChange(permission, event.target.value === 'true')}
                                                                        value={role.permissions!.some(rolePermission => permission.id === rolePermission.id)}/>
                                                    </Can>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}
                        </LoadingOverlay>
                    </Grid>
                )}
            </Grid>

            <ConfirmDelete onClose={handleDeleteClose} onDeleteClick={onDeleteClick} open={openDelete}/>
        </form>
    );
};

export default RoleEdit;
